<template>
  <div
    :class="[$style.base, $style[`color-${color}`], $style[`size-${size}`], { [$style.absolutePosition]: !!position }]"
    :style="style"
  >
    <slot />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  inheritAttrs: false,
});
</script>

<script
  setup
  lang="ts"
>
import type { StyleValue } from 'vue';
import { computed } from 'vue';

type Sides = 'top' | 'right' | 'bottom' | 'left';

const props = withDefaults(
  defineProps<{
    color?: 'accent' | 'error' | 'primary' | 'inverted' | 'fixed';
    position?: Partial<Record<Sides, number>>;
    size?: 's' | 'm';
  }>(),
  {
    color: 'accent',
    size: 's',
  },
);

const style: ComputedRef<StyleValue> = computed(() => {
  const pos = props.position;
  const out: Record<string, string> = {};

  if (pos) {
    for (const [k, v] of Object.entries(pos) as [Sides, number | undefined][]) {
      if (v != null) out[k] = `${Math.round(v)}px`;
    }
  }

  return out;
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  z-index: 1;

  &.absolutePosition {
    position: absolute;
  }

  &:not(.absolutePosition) {
    position: relative;
  }

  &.color-accent {
    background: var(--color-accent);
  }

  &.color-error {
    background: var(--color-error);
  }

  &.color-primary {
    background: var(--color-primary);

    @include theme('light') {
      color: var(--color-text-inverted);
    }

    @include theme('dark') {
      background: #fff;
      color: var(--color-primary);
    }
  }

  &.color-inverted {
    background: white;
    color: var(--color-primary);
  }

  // Dark in both themes, for use on a pink fill such as the active nav item
  &.color-fixed {
    background: var(--text-fixed);
    color: var(--color-text-inverted);
  }

  &.size-s {
    font-size: 10px;
    height: 15px;
    min-width: 15px;
    border-radius: 8px;
  }

  &.size-m {
    font-size: 12px;
    height: 20px;
    min-width: 20px;
    border-radius: 10px;
    font-weight: bold;
  }

  &:empty {
    height: 8px !important;
    width: 8px !important;
    min-width: 8px !important;
    min-height: 8px !important;
  }
}
</style>
